import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { ScrollPanel } from 'primereact/scrollpanel';
import { WorkflowExecution } from '../types';

interface WorkflowErrorHandlerProps {
  workflow: WorkflowExecution;
  onRetry?: () => Promise<string | null>;
  onCancel?: () => Promise<boolean>;
  onContactSupport?: (errorDetails: string) => void;
}

interface ErrorSolution {
  title: string;
  description: string;
  action?: () => void;
  actionLabel?: string;
  severity: 'info' | 'warn' | 'error';
}

const WorkflowErrorHandler: React.FC<WorkflowErrorHandlerProps> = ({
  workflow,
  onRetry,
  onCancel,
  onContactSupport
}) => {
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const getErrorCategory = (error?: string): string => {
    if (!error) return 'unknown';
    
    const errorLower = error.toLowerCase();
    
    if (errorLower.includes('timeout') || errorLower.includes('timed out')) {
      return 'timeout';
    }
    if (errorLower.includes('kobo') || errorLower.includes('api')) {
      return 'api';
    }
    if (errorLower.includes('validation') || errorLower.includes('invalid')) {
      return 'validation';
    }
    if (errorLower.includes('permission') || errorLower.includes('unauthorized')) {
      return 'permission';
    }
    if (errorLower.includes('network') || errorLower.includes('connection')) {
      return 'network';
    }
    
    return 'system';
  };

  const getErrorSolutions = (category: string): ErrorSolution[] => {
    switch (category) {
      case 'timeout':
        return [
          {
            title: 'Retry the workflow',
            description: 'The operation timed out, likely due to high server load. Retrying usually resolves this.',
            action: handleRetry,
            actionLabel: 'Retry Now',
            severity: 'info'
          },
          {
            title: 'Check your internet connection',
            description: 'Ensure you have a stable internet connection before retrying.',
            severity: 'warn'
          },
          {
            title: 'Try again later',
            description: 'If the problem persists, the server may be experiencing high load. Try again in a few minutes.',
            severity: 'info'
          }
        ];
      
      case 'api':
        return [
          {
            title: 'Check KoboToolbox account',
            description: 'Verify that your KoboToolbox account is active and the API token is valid.',
            severity: 'warn'
          },
          {
            title: 'Validate account permissions',
            description: 'Ensure your KoboToolbox account has permission to create and deploy forms.',
            severity: 'warn'
          },
          {
            title: 'Retry the workflow',
            description: 'API issues are often temporary. Retrying may resolve the problem.',
            action: handleRetry,
            actionLabel: 'Retry Now',
            severity: 'info'
          }
        ];
      
      case 'validation':
        return [
          {
            title: 'Check your TOR document',
            description: 'Ensure your Terms of Reference document contains clear, measurable objectives.',
            severity: 'warn'
          },
          {
            title: 'Review project settings',
            description: 'Verify that all required project information is complete and accurate.',
            severity: 'warn'
          },
          {
            title: 'Upload a new TOR',
            description: 'Try uploading a different or updated version of your TOR document.',
            severity: 'info'
          }
        ];
      
      case 'permission':
        return [
          {
            title: 'Check KoboToolbox permissions',
            description: 'Verify that your KoboToolbox account has the necessary permissions to create forms.',
            severity: 'error'
          },
          {
            title: 'Re-validate your account',
            description: 'Go to your profile settings and re-validate your KoboToolbox account.',
            severity: 'warn'
          },
          {
            title: 'Contact your KoboToolbox administrator',
            description: 'If using an organizational account, contact your administrator for permission updates.',
            severity: 'info'
          }
        ];
      
      case 'network':
        return [
          {
            title: 'Check your internet connection',
            description: 'Ensure you have a stable internet connection.',
            severity: 'warn'
          },
          {
            title: 'Retry the workflow',
            description: 'Network issues are often temporary. Retrying may resolve the problem.',
            action: handleRetry,
            actionLabel: 'Retry Now',
            severity: 'info'
          },
          {
            title: 'Try from a different network',
            description: 'If the problem persists, try accessing from a different network or location.',
            severity: 'info'
          }
        ];
      
      default:
        return [
          {
            title: 'Retry the workflow',
            description: 'Many issues are temporary and can be resolved by retrying the workflow.',
            action: handleRetry,
            actionLabel: 'Retry Now',
            severity: 'info'
          },
          {
            title: 'Check system status',
            description: 'Verify that all external services (KoboToolbox) are operational.',
            severity: 'warn'
          },
          {
            title: 'Contact support',
            description: 'If the problem persists, contact our support team with the error details.',
            action: () => onContactSupport?.(getErrorDetails()),
            actionLabel: 'Contact Support',
            severity: 'info'
          }
        ];
    }
  };

  const handleRetry = async () => {
    if (!onRetry) return;
    
    try {
      setRetrying(true);
      const newExecutionArn = await onRetry();
      if (newExecutionArn) {
        // Success handled by parent component
      }
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setRetrying(false);
    }
  };

  const handleCancel = async () => {
    if (!onCancel) return;
    
    try {
      setCancelling(true);
      await onCancel();
    } catch (error) {
      console.error('Cancel failed:', error);
    } finally {
      setCancelling(false);
    }
  };

  const getErrorDetails = (): string => {
    return `
Workflow Execution Details:
- Execution ARN: ${workflow.execution_arn}
- Status: ${workflow.status}
- Started: ${workflow.started_at}
- Current Step: ${workflow.current_step || 'Unknown'}
- Error: ${workflow.error || 'No error message'}
- Cause: ${workflow.cause || 'No cause specified'}

Browser Information:
- User Agent: ${navigator.userAgent}
- Timestamp: ${new Date().toISOString()}
    `.trim();
  };

  const errorCategory = getErrorCategory(workflow.error);
  const solutions = getErrorSolutions(errorCategory);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'pi pi-times-circle';
      case 'warn':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-info-circle';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return '#dc2626';
      case 'warn':
        return '#d97706';
      default:
        return '#2563eb';
    }
  };

  return (
    <Card className="workflow-error-handler">
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          width: '3rem',
          height: '3rem',
          borderRadius: '50%',
          backgroundColor: '#fef2f2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626', fontSize: '1.25rem' }}></i>
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '1.125rem',
            margin: '0 0 0.5rem 0',
            color: '#1f2937'
          }}>
            Workflow Failed
          </h3>
          <p style={{
            color: '#6b7280',
            margin: '0 0 1rem 0',
            lineHeight: 1.5
          }}>
            The workflow encountered an error and could not complete. Here are some steps you can take to resolve the issue.
          </p>
          
          {workflow.error && (
            <Message
              severity="error"
              text={workflow.error}
              style={{ marginBottom: '1rem' }}
            />
          )}
          
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            {onRetry && (
              <Button
                label="Retry Workflow"
                icon="pi pi-refresh"
                onClick={handleRetry}
                loading={retrying}
                severity="info"
              />
            )}
            <Button
              label="Troubleshooting Guide"
              icon="pi pi-question-circle"
              onClick={() => setShowTroubleshooting(true)}
              outlined
            />
            {onCancel && (
              <Button
                label="Cancel Workflow"
                icon="pi pi-times"
                onClick={handleCancel}
                loading={cancelling}
                severity="danger"
                outlined
              />
            )}
          </div>
        </div>
      </div>

      {/* Quick Solutions */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{
          fontSize: '1rem',
          margin: '0 0 1rem 0',
          color: '#1f2937'
        }}>
          Recommended Solutions
        </h4>
        <div style={{
          display: 'grid',
          gap: '0.75rem'
        }}>
          {solutions.slice(0, 2).map((solution, index) => (
            <div
              key={index}
              style={{
                padding: '1rem',
                border: `1px solid ${getSeverityColor(solution.severity)}20`,
                borderRadius: '8px',
                backgroundColor: `${getSeverityColor(solution.severity)}05`
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem'
              }}>
                <i
                  className={getSeverityIcon(solution.severity)}
                  style={{
                    color: getSeverityColor(solution.severity),
                    fontSize: '1rem',
                    marginTop: '0.125rem'
                  }}
                ></i>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#1f2937',
                    marginBottom: '0.25rem'
                  }}>
                    {solution.title}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    marginBottom: solution.action ? '0.75rem' : 0
                  }}>
                    {solution.description}
                  </div>
                  {solution.action && solution.actionLabel && (
                    <Button
                      label={solution.actionLabel}
                      size="small"
                      onClick={solution.action}
                      loading={solution.actionLabel === 'Retry Now' ? retrying : false}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Error Details */}
      <Accordion>
        <AccordionTab header="Error Details">
          <div style={{
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <div><strong>Status:</strong> {workflow.status}</div>
            <div><strong>Started:</strong> {new Date(workflow.started_at).toLocaleString()}</div>
            {workflow.ended_at && (
              <div><strong>Failed:</strong> {new Date(workflow.ended_at).toLocaleString()}</div>
            )}
            <div><strong>Current Step:</strong> {workflow.current_step || 'Unknown'}</div>
            {workflow.error && (
              <div><strong>Error:</strong> {workflow.error}</div>
            )}
            {workflow.cause && (
              <div><strong>Cause:</strong> {workflow.cause}</div>
            )}
            <div><strong>Execution ARN:</strong> {workflow.execution_arn}</div>
          </div>
        </AccordionTab>
      </Accordion>

      {/* Troubleshooting Dialog */}
      <Dialog
        header="Troubleshooting Guide"
        visible={showTroubleshooting}
        onHide={() => setShowTroubleshooting(false)}
        style={{ width: '60vw', minWidth: '500px' }}
        modal
      >
        <ScrollPanel style={{ width: '100%', height: '400px' }}>
          <div style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <Tag
                value={`Error Category: ${errorCategory.toUpperCase()}`}
                severity="info"
                style={{ marginBottom: '1rem' }}
              />
              <p style={{
                color: '#6b7280',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                Based on the error details, we've identified this as a <strong>{errorCategory}</strong> related issue. 
                Here are all the recommended solutions:
              </p>
            </div>

            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {solutions.map((solution, index) => (
                <div
                  key={index}
                  style={{
                    padding: '1rem',
                    border: `1px solid ${getSeverityColor(solution.severity)}20`,
                    borderRadius: '8px',
                    backgroundColor: `${getSeverityColor(solution.severity)}05`
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}>
                    <i
                      className={getSeverityIcon(solution.severity)}
                      style={{
                        color: getSeverityColor(solution.severity),
                        fontSize: '1rem',
                        marginTop: '0.125rem'
                      }}
                    ></i>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#1f2937',
                        marginBottom: '0.5rem'
                      }}>
                        {index + 1}. {solution.title}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginBottom: solution.action ? '0.75rem' : 0,
                        lineHeight: 1.4
                      }}>
                        {solution.description}
                      </div>
                      {solution.action && solution.actionLabel && (
                        <Button
                          label={solution.actionLabel}
                          size="small"
                          onClick={() => {
                            solution.action!();
                            setShowTroubleshooting(false);
                          }}
                          loading={solution.actionLabel === 'Retry Now' ? retrying : false}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '2rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h5 style={{
                fontSize: '0.875rem',
                margin: '0 0 0.5rem 0',
                color: '#1f2937'
              }}>
                Still having issues?
              </h5>
              <p style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                margin: '0 0 0.75rem 0'
              }}>
                If none of these solutions work, you can contact our support team with the error details.
              </p>
              <Button
                label="Contact Support"
                icon="pi pi-envelope"
                size="small"
                onClick={() => {
                  onContactSupport?.(getErrorDetails());
                  setShowTroubleshooting(false);
                }}
                outlined
              />
            </div>
          </div>
        </ScrollPanel>
      </Dialog>
    </Card>
  );
};

export default WorkflowErrorHandler;